import React, { useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import Select from 'react-select';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import incident_manager from '../api/incident';
import { format_date_short } from '../util/utils';
import {
    buildIncidentMergeRequest,
    getDefaultVeloviewerUrl,
    getIncidentMergeLabel,
    getIncidentVeloviewerUrl,
} from '../util/incidentMerge';

const getApiErrorMessage = (error) =>
    error?.response?.data?.DETAIL
    || error?.response?.data?.MESSAGE
    || error?.message
    || 'The incidents could not be merged.';

const getIncidentDateWindow = (incident, daysBefore = 7, daysAfter = 7) => {
    const incidentDate = new Date(incident.incident_timestamp || incident.incident_ts);
    if (Number.isNaN(incidentDate.getTime())) {
        throw new Error('The incident date could not be read.');
    }

    const dateFrom = new Date(incidentDate);
    dateFrom.setDate(dateFrom.getDate() - daysBefore);

    const dateTo = new Date(incidentDate);
    dateTo.setDate(dateTo.getDate() + daysAfter);

    return {
        date_from: format_date_short(dateFrom),
        date_to: format_date_short(dateTo),
    };
};

const normalizeEditableValue = (value) => value ?? '';

const getMergeTextFields = (incident) => ({
    incident_description: normalizeEditableValue(incident?.incident_description),
    incident_summary: normalizeEditableValue(incident?.incident_summary),
    incident_SAFERcomment: normalizeEditableValue(incident?.incident_SAFERcomment),
    incident_SAFERaction: normalizeEditableValue(incident?.incident_SAFERaction),
    incident_veloviewer_url: normalizeEditableValue(incident?.incident_veloviewer_url),
});

const getCauseSelectionId = (origin, cause) =>
    `${origin}:${cause.cause_id}:${cause.subcause_id || ''}:${cause.incident_cause_description || ''}`;

const getRiderSelectionId = (origin, riderIncident) =>
    `${origin}:${riderIncident.rider?.rider_id || riderIncident.rider_id}`;

const getCauseDuplicateKey = (item) => item.cause.cause_id;

const getRiderDuplicateKey = (item) => item.rider.rider?.rider_id || item.rider.rider_id;

const getDefaultDuplicateSelections = (items, getDuplicateKey) => {
    const targetKeys = new Set(
        items
            .filter((item) => item.origin === 'target')
            .map(getDuplicateKey)
    );

    return items.reduce((selections, item) => ({
        ...selections,
        [item.selectionId]: item.origin === 'target' || !targetKeys.has(getDuplicateKey(item)),
    }), {});
};

const getCauseLabel = (cause) => [
    cause.cause_description,
    cause.subcause_description,
].filter(Boolean).join(' / ') || `Cause ${cause.cause_id}`;

const getRiderLabel = (riderIncident) => {
    const rider = riderIncident.rider || riderIncident;
    return [
        [rider.first_name, rider.last_name].filter(Boolean).join(' '),
        rider.uci_id ? `UCI ${rider.uci_id}` : null,
        rider.resolved_team?.team_name,
    ].filter(Boolean).join(' - ') || `Rider ${rider.rider_id}`;
};

const IncidentSummary = ({ incident, role }) => {
    if (!incident) return null;

    const riders = Object.values(incident.riders || {});
    const causes = Object.values(incident.causes || {});

    return (
        <section className={`merge-incident-card merge-incident-card--${role}`}>
            <h3>{role === 'source' ? 'Incident A — will be removed' : 'Incident B — will be kept'}</h3>
            <dl>
                <dt>Incident</dt>
                <dd>{getIncidentMergeLabel(incident)}</dd>
                <dt>Description</dt>
                <dd>{incident.incident_description || 'Empty'}</dd>
                <dt>Description AI</dt>
                <dd>{incident.incident_summary || 'Empty'}</dd>
                <dt>Involved riders</dt>
                <dd>{riders.length}</dd>
                <dt>Causes</dt>
                <dd>{causes.length}</dd>
                <dt>VeloViewer URL</dt>
                <dd>{getIncidentVeloviewerUrl(incident) || 'Empty'}</dd>
            </dl>
        </section>
    );
};

const MergeSelectionList = ({ title, emptyLabel, items, selections, onToggle, getItemId, renderItem }) => (
    <section className="merge-selection-list">
        <h4>{title}</h4>
        {items.length > 0 ? (
            <ul>
                {items.map((item) => {
                    const id = getItemId(item);
                    return (
                        <li key={id}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={selections[id] !== false}
                                    onChange={(event) => onToggle(id, event.target.checked)}
                                />
                                <span>{renderItem(item)}</span>
                            </label>
                        </li>
                    );
                })}
            </ul>
        ) : (
            <p><i>{emptyLabel}</i></p>
        )}
    </section>
);

const MergeTargetEditor = ({
    sourceIncident,
    targetIncident,
    mergeFields,
    setMergeFields,
    causeItems,
    riderItems,
    causeSelections,
    riderSelections,
    onToggleCause,
    onToggleRider,
    submitting,
}) => {
    if (!targetIncident) return null;

    const updateField = (fieldName, value) => {
        setMergeFields((previous) => ({ ...previous, [fieldName]: value }));
    };
    const sourceVeloviewerUrl = getIncidentVeloviewerUrl(sourceIncident);
    const targetVeloviewerUrl = getIncidentVeloviewerUrl(targetIncident);
    const veloviewerOptions = [
        targetVeloviewerUrl ? { label: 'Use URL from Incident B', value: targetVeloviewerUrl } : null,
        sourceVeloviewerUrl ? { label: 'Use URL from Incident A', value: sourceVeloviewerUrl } : null,
    ].filter(Boolean);
    const isCustomVeloviewerUrl = !veloviewerOptions.some((option) => option.value === mergeFields.incident_veloviewer_url);

    return (
        <section className="merge-incident-editor">
            <h3>Incident B merge result</h3>
            <div className="form-group">
                <label htmlFor="merge-description">Description</label>
                <textarea
                    id="merge-description"
                    className="form-control"
                    value={mergeFields.incident_description}
                    onChange={(event) => updateField('incident_description', event.target.value)}
                    disabled={submitting}
                    rows={5}
                />
            </div>
            <div className="form-group">
                <label htmlFor="merge-summary">Description AI</label>
                <textarea
                    id="merge-summary"
                    className="form-control"
                    value={mergeFields.incident_summary}
                    onChange={(event) => updateField('incident_summary', event.target.value)}
                    disabled={submitting}
                    rows={5}
                />
            </div>
            <div className="form-group">
                <label htmlFor="merge-safer-comment">SAFER comment</label>
                <textarea
                    id="merge-safer-comment"
                    className="form-control"
                    value={mergeFields.incident_SAFERcomment}
                    onChange={(event) => updateField('incident_SAFERcomment', event.target.value)}
                    disabled={submitting}
                    rows={3}
                />
            </div>
            <div className="form-group">
                <label htmlFor="merge-safer-action">SAFER action</label>
                <textarea
                    id="merge-safer-action"
                    className="form-control"
                    value={mergeFields.incident_SAFERaction}
                    onChange={(event) => updateField('incident_SAFERaction', event.target.value)}
                    disabled={submitting}
                    rows={3}
                />
            </div>
            <div className="form-group">
                <label htmlFor="merge-veloviewer-url">VeloViewer URL</label>
                {veloviewerOptions.length > 0 && (
                    <div className="merge-veloviewer-options">
                        {veloviewerOptions.map((option) => (
                            <label key={option.label}>
                                <input
                                    type="radio"
                                    name="merge-veloviewer-option"
                                    value={option.value}
                                    checked={mergeFields.incident_veloviewer_url === option.value}
                                    onChange={(event) => updateField('incident_veloviewer_url', event.target.value)}
                                    disabled={submitting}
                                />
                                <span>{option.label}</span>
                            </label>
                        ))}
                        <label>
                            <input
                                type="radio"
                                name="merge-veloviewer-option"
                                checked={isCustomVeloviewerUrl}
                                onChange={() => updateField('incident_veloviewer_url', '')}
                                disabled={submitting}
                            />
                            <span>Use custom URL</span>
                        </label>
                    </div>
                )}
                <input
                    id="merge-veloviewer-url"
                    className="form-control"
                    type="text"
                    value={mergeFields.incident_veloviewer_url}
                    onChange={(event) => updateField('incident_veloviewer_url', event.target.value)}
                    disabled={submitting}
                />
            </div>

            <MergeSelectionList
                title="Causes to keep"
                emptyLabel="No causes on either incident."
                items={causeItems}
                selections={causeSelections}
                onToggle={onToggleCause}
                getItemId={(item) => item.selectionId}
                renderItem={(item) => (
                    <>
                        <b>{item.origin === 'target' ? 'B' : 'A'}</b> {getCauseLabel(item.cause)}
                        {item.cause.incident_cause_description ? ` - ${item.cause.incident_cause_description}` : ''}
                    </>
                )}
            />
            <MergeSelectionList
                title="Involved riders to keep"
                emptyLabel="No riders on either incident."
                items={riderItems}
                selections={riderSelections}
                onToggle={onToggleRider}
                getItemId={(item) => item.selectionId}
                renderItem={(item) => (
                    <>
                        <b>{item.origin === 'target' ? 'B' : 'A'}</b> {getRiderLabel(item.rider)}
                        {item.rider.incident_rider_implications ? ` - ${item.rider.incident_rider_implications}` : ''}
                    </>
                )}
            />
        </section>
    );
};

export default function MergeIncident() {
    const { incident_id: sourceIncidentId } = useParams();
    const history = useHistory();
    const [sourceIncident, setSourceIncident] = useState(null);
    const [targetIncident, setTargetIncident] = useState(null);
    const [incidentOptions, setIncidentOptions] = useState([]);
    const [selectedTarget, setSelectedTarget] = useState(null);
    const [mergeFields, setMergeFields] = useState(getMergeTextFields(null));
    const [causeSelections, setCauseSelections] = useState({});
    const [riderSelections, setRiderSelections] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingTarget, setLoadingTarget] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        let active = true;

        incident_manager.get_incident(Number(sourceIncidentId)).then((source) => {
            if (!active) return;

            if (!source) {
                setMessage(`Incident ${sourceIncidentId} could not be loaded.`);
                return;
            }

            setSourceIncident(source);
            return incident_manager.search_incidents(getIncidentDateWindow(source));
        }).then((incidents) => {
            if (!active || !incidents) return;

            const options = Object.values(incidents || {})
                .filter((candidate) => Number(candidate.incident_id) !== Number(sourceIncidentId))
                .map((candidate) => ({
                    value: Number(candidate.incident_id),
                    label: getIncidentMergeLabel(candidate),
                }));

            setIncidentOptions(options);
        }).catch((error) => {
            if (active) setMessage(getApiErrorMessage(error));
        }).finally(() => {
            if (active) setLoading(false);
        });

        return () => {
            active = false;
        };
    }, [sourceIncidentId]);

    const selectTarget = async (option) => {
        setSelectedTarget(option);
        setTargetIncident(null);
        setMergeFields(getMergeTextFields(null));
        setCauseSelections({});
        setRiderSelections({});
        setMessage('');

        if (!option) return;

        setLoadingTarget(true);
        try {
            const target = await incident_manager.get_incident(option.value);
            if (!target) throw new Error(`Incident ${option.value} could not be loaded.`);

            setTargetIncident(target);
            const defaultVeloviewerUrl = getDefaultVeloviewerUrl(sourceIncident, target);
            setMergeFields({
                ...getMergeTextFields(target),
                incident_veloviewer_url: defaultVeloviewerUrl || '',
            });
        } catch (error) {
            setSelectedTarget(null);
            setMessage(getApiErrorMessage(error));
        } finally {
            setLoadingTarget(false);
        }
    };

    const causeItems = useMemo(() => {
        const buildItems = (incident, origin) => Object.values(incident?.causes || {}).map((cause) => ({
            origin,
            incident_id: Number(incident.incident_id),
            cause,
            selectionId: getCauseSelectionId(origin, cause),
        }));
        return [
            ...buildItems(targetIncident, 'target'),
            ...buildItems(sourceIncident, 'source'),
        ];
    }, [sourceIncident, targetIncident]);

    const riderItems = useMemo(() => {
        const buildItems = (incident, origin) => Object.values(incident?.riders || {}).map((rider) => ({
            origin,
            incident_id: Number(incident.incident_id),
            rider,
            selectionId: getRiderSelectionId(origin, rider),
        }));
        return [
            ...buildItems(targetIncident, 'target'),
            ...buildItems(sourceIncident, 'source'),
        ];
    }, [sourceIncident, targetIncident]);

    useEffect(() => {
        setCauseSelections(getDefaultDuplicateSelections(causeItems, getCauseDuplicateKey));
    }, [causeItems]);

    useEffect(() => {
        setRiderSelections(getDefaultDuplicateSelections(riderItems, getRiderDuplicateKey));
    }, [riderItems]);

    const toggleCauseSelection = (selectionId, checked) => {
        const selectedItem = causeItems.find((item) => item.selectionId === selectionId);
        if (!selectedItem) return;

        setCauseSelections((previous) => {
            const next = { ...previous, [selectionId]: checked };
            if (checked) {
                const duplicateKey = getCauseDuplicateKey(selectedItem);
                causeItems.forEach((item) => {
                    if (
                        item.selectionId !== selectionId
                        && item.origin !== selectedItem.origin
                        && getCauseDuplicateKey(item) === duplicateKey
                    ) {
                        next[item.selectionId] = false;
                    }
                });
            }
            return next;
        });
    };

    const toggleRiderSelection = (selectionId, checked) => {
        const selectedItem = riderItems.find((item) => item.selectionId === selectionId);
        if (!selectedItem) return;

        setRiderSelections((previous) => {
            const next = { ...previous, [selectionId]: checked };
            if (checked) {
                const duplicateKey = getRiderDuplicateKey(selectedItem);
                riderItems.forEach((item) => {
                    if (
                        item.selectionId !== selectionId
                        && item.origin !== selectedItem.origin
                        && getRiderDuplicateKey(item) === duplicateKey
                    ) {
                        next[item.selectionId] = false;
                    }
                });
            }
            return next;
        });
    };

    const getTargetUpdates = () => ({
        incident_description: mergeFields.incident_description,
        incident_summary: mergeFields.incident_summary,
        incident_SAFERcomment: mergeFields.incident_SAFERcomment,
        incident_SAFERaction: mergeFields.incident_SAFERaction,
        incident_veloviewer_url: mergeFields.incident_veloviewer_url,
    });

    const getIncidentCauses = () => causeItems
        .filter((item) => causeSelections[item.selectionId] !== false)
        .map((item) => ({
            cause_id: item.cause.cause_id,
            cause_description: item.cause.cause_description,
            subcause_id: item.cause.subcause_id || null,
            subcause_description: item.cause.subcause_description || null,
            incident_cause_description: item.cause.incident_cause_description || null,
        }));

    const getIncidentInvolvedRiders = () => riderItems
        .filter((item) => riderSelections[item.selectionId] !== false)
        .map((item) => {
            const rider = item.rider.rider || item.rider;
            return {
                rider_name: [rider.first_name, rider.last_name].filter(Boolean).join(' '),
                rider_team: rider.resolved_team
                    ? `${rider.resolved_team.team_name} (${rider.resolved_team.team_code})`
                    : 'unknown',
                rider_id: rider.rider_id,
                incident_rider_notes: item.rider.incident_rider_notes || null,
                incident_rider_implications: item.rider.incident_rider_implications || null,
                incident_rider_severity: item.rider.incident_rider_severity || null,
                incident_rider_card: item.rider.incident_rider_card || null,
                incident_rider_fault: item.rider.incident_rider_fault || false,
                incident_rider_injury: item.rider.incident_rider_injury || null,
            };
        });

    const performMerge = async () => {
        setSubmitting(true);
        setMessage('');

        try {
            const request = buildIncidentMergeRequest({
                sourceIncident,
                targetIncident,
                selectedVeloviewerUrl: mergeFields.incident_veloviewer_url,
                targetUpdates: getTargetUpdates(),
                incidentCauses: getIncidentCauses(),
                incidentInvolvedRiders: getIncidentInvolvedRiders(),
            });
            await incident_manager.merge_incidents(sourceIncident.incident_id, request);
            history.replace(`/incident/${targetIncident.incident_id}`);
        } catch (error) {
            setMessage(getApiErrorMessage(error));
            setSubmitting(false);
        }
    };

    const confirmMerge = () => {
        try {
            buildIncidentMergeRequest({
                sourceIncident,
                targetIncident,
                selectedVeloviewerUrl: mergeFields.incident_veloviewer_url,
                targetUpdates: getTargetUpdates(),
                incidentCauses: getIncidentCauses(),
                incidentInvolvedRiders: getIncidentInvolvedRiders(),
            });
        } catch (error) {
            setMessage(error.message);
            return;
        }

        confirmAlert({
            title: 'Merge incidents?',
            message: `Incident ${sourceIncident.incident_id} will be merged into incident ${targetIncident.incident_id} and then permanently removed.`,
            buttons: [
                { label: 'Merge incidents', onClick: performMerge },
                { label: 'Cancel' },
            ],
        });
    };

    if (loading) {
        return <div className="merge-incident"><h1>Merge incident</h1><p>Loading incidents...</p></div>;
    }

    return (
        <>
        <h1>Merge incident</h1>
        <div className="merge-incident">
            {message && <div className="alert alert-danger" role="alert">{message}</div>}

            {sourceIncident && (
                <>
                    <p className="merge-incident-intro">
                        Select the destination incident, then edit the fields that should remain on Incident B.
                        You can copy parts from Incident A and choose which causes and riders are included in the
                        final merged incident.
                    </p>

                    <div className="merge-incident-selector form-group">
                        <label htmlFor="merge-target">Destination incident (Incident B)</label>
                        <Select
                            inputId="merge-target"
                            value={selectedTarget}
                            options={incidentOptions}
                            onChange={selectTarget}
                            isClearable
                            isDisabled={loadingTarget || submitting}
                            placeholder="Search by incident ID, race, or date..."
                        />
                    </div>

                    {loadingTarget && <p>Loading destination incident...</p>}

                    <div className="merge-incident-comparison">
                        <IncidentSummary incident={sourceIncident} role="source" />
                        <MergeTargetEditor
                            sourceIncident={sourceIncident}
                            targetIncident={targetIncident}
                            mergeFields={mergeFields}
                            setMergeFields={setMergeFields}
                            causeItems={causeItems}
                            riderItems={riderItems}
                            causeSelections={causeSelections}
                            riderSelections={riderSelections}
                            onToggleCause={toggleCauseSelection}
                            onToggleRider={toggleRiderSelection}
                            submitting={submitting}
                        />
                    </div>

                    <div className="merge-incident-actions">
                        <Link className="btn btn-secondary" to={`/incident/${sourceIncident.incident_id}`}>Cancel</Link>
                        <button
                            className="btn btn-danger"
                            type="button"
                            onClick={confirmMerge}
                            disabled={!targetIncident || submitting}
                        >
                            {submitting ? 'Merging...' : 'Merge A into B'}
                        </button>
                    </div>
                </>
            )}
        </div>
        </>
    );
}
